import { type fabric } from 'fabric';
import type SimpleGroup from 'fabric/SimpleGroup';
import type SimpleSelection from '../fabric/SimpleSelection';
import type Comment from './Comment';
import type CommentableBehaviors from './CommentableBehaviors';
import type Frame from './Frame';
import type LabelBehaviors from './LabelBehaviors';
import type Shape from './Shape';
import type ShapeRecord from '../shapes/ShapeRecord';
import type ReadOnlyBehaviors from './ReadOnlyBehaviors';
import type SmartGuideBehaviors from './SmartGuideBehaviors';
import type Table from './Table';
import type DataStoreRecordBehaviors from '../collaboration/DataStoreRecordBehaviors';
import type Connector from './Connector';
import type LockBehaviors from './LockBehaviors';
import type EndlessStackBehaviors from './EndlessStackBehaviors';
import type EmbeddedTextBehaviors from './EmbeddedTextBehaviorsV2';
import type ReactionsBehaviors from './ReactionsBehaviors';
import type StyleCopyPasteBehaviors from './StyleCopyPasteBehaviors';
import type Wireframe from './Wireframe';
import type Device from './Device';
import type {
  SmartRecordCreatableType,
  IntegrationShape,
  RecordReference,
  HasEmbeddedText,
} from '../types';
import type FrameMemberBehaviors from './FrameMemberBehaviors';
import type ShapePersistentControlBehaviors from './ShapePersistentControlBehaviors';
import type VotingBehaviors from './VotingBehaviors';
import type CanvaSXBehaviors from './CanvaSXBehaviors';
import type EmbeddedBookmark from './EmbeddedBookmark';
import type EmbeddedAssistantArtifact from './EmbeddedAssistantArtifact';
import type ShapeIntegrationRecord from '../shapes/ShapeIntegrationRecord';
import type SmartZoneBehaviors from './SmartZoneBehaviors';
import type {
  WhiteboardRecordInfo,
  WhiteboardRecordWithLinks,
} from '../shared-types';
import type ShapeAnnotation from '../shapes/ShapeAnnotation';
import type HighlightBehaviors from './HighlightBehaviors';
import type ConnectorAttachmentBehaviors from './ConnectorAttachmentBehaviors';
import type TextField from './TextField';
import type ShapeSpriteIcon from '../shapes/ShapeSpriteIcon';
import { recordTypeHasCardLayout } from '../cardLayouts';
import type ShapeIcon from '../shapes/ShapeIcon';
import type LayoutBehaviors from './LayoutBehaviors';
import { normalizeRecordType } from '../utils/record';
import { creatableRecordTypes } from '../types';
import type BusinessProcessBase from '../shapes/BusinessProcessBase';
import { businessProcessShapes } from '../businessProcess/definitions';
import type ShapeSmartRecord from '../shapes/ShapeSmartRecord';
import type FrameBehaviors from './FrameBehaviors';
import type ShapeImage from '../shapes/ShapeImage';
import type SmartObject from './SmartObject';
import type TitleBehaviors from './TitleBehaviors';
import { type LinkToUrlBehaviorsInterface } from './LinkToUrlBehaviors';
import type MindMapBranch from './MindMapBranch';
import type MindMap from './MindMap';
import type MindMapLeaf from './MindMapLeaf';
import type MindMapConnector from './MindMapConnector';
import type PresentationFrame from './PresentationFrame';
import { type EmbeddedContentBehaviorsInterface } from './EmbeddedContentBehaviors';

// guards for specific fabric.Object subclasses

export const isFrame = (obj: fabric.Object): obj is Frame =>
  obj.type === 'frame';

export const isPresentationFrame = (
  obj: fabric.Object
): obj is PresentationFrame => obj.type === 'presentationFrame';

// similar to the above, but includes any possible subclasses of frame
export const isFrameLike = (obj: fabric.Object): obj is Frame =>
  obj.isFrameLike();

export const isSimpleGroup = (obj: fabric.Object): obj is SimpleGroup =>
  obj.type === 'group';

export const isSimpleSelection = (obj: fabric.Object): obj is SimpleSelection =>
  obj.type === 'activeSelection';

export const isGroupLike = (obj: fabric.Object): obj is SimpleGroup =>
  isSimpleGroup(obj) || isSimpleSelection(obj);

export const isComment = (obj: fabric.Object): obj is Comment =>
  obj.type === 'comment';

export const isTable = (obj: fabric.Object): obj is Table =>
  obj.type === 'table';

export const isTextField = (obj: fabric.Object): obj is TextField =>
  obj.type === 'textField';

export const hasSelectedTableCell = (obj: fabric.Object): obj is Table =>
  isTable(obj) && obj.hasSelectedCell();

export const isConnector = (obj?: fabric.Object): obj is Connector =>
  obj?.type === 'connector';

export const isShape = (obj: fabric.Object): obj is Shape =>
  obj.type === 'shape';

export const isDependencyConnector = (obj?: fabric.Object): obj is Connector =>
  isConnector(obj) && !!obj.dependencyLineInfo;

export const isShapeRecord = (obj: fabric.Object): obj is Shape<ShapeRecord> =>
  isShape(obj) && obj.shapeName === 'record';

export const isShapeIcon = (obj: fabric.Object): obj is Shape<ShapeIcon> =>
  isShape(obj) && obj.shapeName === 'icon';

export const isShapeSpriteIcon = (
  obj: fabric.Object
): obj is Shape<ShapeSpriteIcon> =>
  isShape(obj) && obj.shapeName === 'spriteIcon';

export function hasRecordLinks(
  record?: WhiteboardRecordInfo
): record is WhiteboardRecordWithLinks {
  if (!record) return false;

  // @ts-expect-error sanity check on record constrcutor
  if (typeof record.constructor.hasField !== 'function') return false;

  return (record.constructor as Aha.ApplicationModelConstructor<any>).hasField(
    'parentRecordLinks'
  );
}

export const isShapeAnnotation = (
  obj: fabric.Object
): obj is Shape<ShapeAnnotation> =>
  isShape(obj) && obj.shapeName === 'annotation';

export const isShapeIntegrationRecord = (
  obj: fabric.Object
): obj is Shape<ShapeIntegrationRecord> =>
  isShape(obj) && obj.shapeName === 'integrationRecord';

export const isShapeSmartRecord = (
  obj: fabric.Object
): obj is Shape<ShapeSmartRecord> =>
  isShape(obj) && obj.shapeName === 'smartRecord';

export const isShapeImage = (obj: fabric.Object): obj is Shape<ShapeImage> =>
  isShape(obj) && obj.shapeName === 'image';

export const isWireframe = (obj: fabric.Object): obj is Wireframe =>
  obj.type === 'wireframe';

export const isDevice = (obj: fabric.Object): obj is Device =>
  obj.type === 'device';

export const isBusinessProcessShape = (
  obj: fabric.Object
): obj is Shape<BusinessProcessBase> =>
  isShape(obj) && Object.keys(businessProcessShapes).includes(obj.shapeName);

export const isEmbeddedBookmark = (
  obj: fabric.Object
): obj is EmbeddedBookmark => obj.type === 'embeddedBookmark';

export const isEmbeddedAssistantArtifact = (
  obj: fabric.Object
): obj is EmbeddedAssistantArtifact => obj.type === 'embeddedAssistantArtifact';

export const isSmartObject = (obj: fabric.Object): obj is SmartObject =>
  obj.type === 'smartObject';

export const isMindMap = (obj?: fabric.Object): obj is MindMap =>
  obj?.type === 'mindMap';

export const isMindMapBranch = (obj?: fabric.Object): obj is MindMapBranch =>
  obj?.type === 'mindMapBranch';

export const isMindMapLeaf = (obj?: fabric.Object): obj is MindMapLeaf =>
  obj?.type === 'mindMapLeaf';

export const isMindMapConnector = (
  obj?: fabric.Object
): obj is MindMapConnector => obj?.type === 'mindMapConnector';

export const hasLayout = (
  obj: fabric.Object
): obj is fabric.Object & LayoutBehaviors & FrameBehaviors =>
  hasFrameBehaviors(obj) &&
  ['mindMap', 'mindMapBranch', 'frame', 'device'].includes(obj.type) &&
  (obj as unknown as LayoutBehaviors).layoutEnabled;

// Checks that the object is a shape or mindMapLeaf and has a record reference
export const hasRecordReference = (
  obj?: fabric.Object
): obj is Shape & RecordReference => {
  return Boolean(
    obj &&
      (isShape(obj) || isMindMapLeaf(obj)) &&
      obj.recordType &&
      obj.recordId
  );
};

export const hasIntegrationRecordReference = (
  obj?: fabric.Object
): obj is IntegrationShape => {
  return Boolean(obj && isShape(obj) && obj.integrationRecord);
};

export const canHaveCardLayout = (
  obj?: fabric.Object
): obj is Shape & RecordReference => {
  if (!hasRecordReference(obj)) {
    return false;
  }

  const recordType = normalizeRecordType(obj.recordType);
  return recordTypeHasCardLayout(recordType);
};

export const isLinkableRecordShape = (obj?: fabric.Object) => {
  if (!hasRecordReference(obj)) {
    return false;
  }

  const recordType = normalizeRecordType(obj.recordType);
  return (
    obj.controller.recordLinkManager?.isLinkableRecordType(recordType) ?? false
  );
};

export const isSmartRecordCreatableType = (
  recordType: string
): recordType is SmartRecordCreatableType =>
  creatableRecordTypes.includes(recordType);
// guards for behavior decorators

export function canConvertSmartRecords() {
  // roadamps, develop, teamwork, and ideas accounts can convert smart records
  return (
    window.currentAccount?.roadmapFlavor ||
    window.currentAccount?.ideasFlavor ||
    window.currentAccount?.teamworkOrDevelopFlavor
  );
}

// check that obj is an instance of a class based on a boolean attribute that it adds
const behaviorGuardFactory =
  <Cls, Base = fabric.Object>(attr: string) =>
  (obj?: Base): obj is Cls & Base =>
    obj && typeof obj === 'object' && attr in obj && obj[attr];

export const hasCommentableBehaviors =
  behaviorGuardFactory<CommentableBehaviors>('__hasCommentableBehaviors');

export const hasLabelBehaviors = behaviorGuardFactory<LabelBehaviors>(
  '__hasLabelBehaviors'
);

export const hasReadOnlyBehaviors = behaviorGuardFactory<ReadOnlyBehaviors>(
  '__hasReadOnlyBehaviors'
);

export const hasSmartGuideBehaviors = behaviorGuardFactory<SmartGuideBehaviors>(
  // Using computeGuides to match existing duck-typing
  'computeGuides'
);

export const hasDataStoreRecordBehaviors =
  behaviorGuardFactory<DataStoreRecordBehaviors>(
    '__hasDataStoreRecordBehaviors'
  );

export const hasSmartZoneBehaviors = behaviorGuardFactory<SmartZoneBehaviors>(
  '__hasSmartZoneBehaviors'
);

export const hasLockBehaviors =
  behaviorGuardFactory<LockBehaviors>('__hasLockBehaviors');

export const hasLinkToUrlBehaviors =
  behaviorGuardFactory<LinkToUrlBehaviorsInterface>('__hasLinkToUrlBehaviors');

export const hasEndlessStackBehaviors =
  behaviorGuardFactory<EndlessStackBehaviors>('__hasEndlessStackBehaviors');

export const hasEmbeddedTextBehaviors = behaviorGuardFactory<
  EmbeddedTextBehaviors & HasEmbeddedText
>('__hasEmbeddedTextBehaviors');

export const hasHighlightBehaviors = behaviorGuardFactory<HighlightBehaviors>(
  '__hasHighlightBehaviors'
);

export const hasReactionBehaviors = behaviorGuardFactory<ReactionsBehaviors>(
  '__hasReactionBehaviors'
);

export const hasStyleCopyPasteBehaviors =
  behaviorGuardFactory<StyleCopyPasteBehaviors>('__hasStyleCopyPasteBehaviors');

export const hasFrameMemberBehaviors =
  behaviorGuardFactory<FrameMemberBehaviors>('__hasFrameMemberBehaviors');

export const hasPersistentControlBehaviors =
  behaviorGuardFactory<ShapePersistentControlBehaviors>(
    '__hasPersistentControlBehaviors'
  );

export const hasVotingBehaviors = behaviorGuardFactory<VotingBehaviors>(
  '__hasVotingBehaviors'
);

export const hasCanvaSXBehaviors = behaviorGuardFactory<CanvaSXBehaviors>(
  '__hasCanvaSXBehaviors'
);

export const hasConnectorAttachmentBehaviours =
  behaviorGuardFactory<ConnectorAttachmentBehaviors>(
    '__hasConnectorAttachmentBehaviors'
  );

export const hasFrameBehaviors = behaviorGuardFactory<FrameBehaviors>(
  '__hasFrameBehaviors'
);

export const hasTitleBehaviors = behaviorGuardFactory<TitleBehaviors>(
  '__hasTitleBehaviors'
);

export const hasEmbeddedContentBehaviors =
  behaviorGuardFactory<EmbeddedContentBehaviorsInterface>(
    '__hasEmbeddedContentBehaviors'
  );

// utility guards

export const isNonEmptyString = (
  val: string | undefined | null
): val is string => Boolean(val);

export const isEndlessStack = (obj: fabric.Object): boolean =>
  Boolean('endlessStack' in obj && obj.get('endlessStack'));
